import Modal from 'react-modal';
import styled from 'styled-components';
import React, { FunctionComponent, useState, useEffect } from 'react';
import { CSVLink } from 'react-csv';

import ToRGB from '../../utils/color';
import theme from '../../constants/theme';
import { ISavedOrder } from '../../generated/graphql';

Modal.setAppElement('#root');

const { r, g, b } = ToRGB(theme.colors.blueKobelco);
const { r: rd, g: gd, b: bd } = ToRGB(theme.colors.blueDark);

const customStyles: Modal.Styles = {
  content: {
    top: undefined,
    left: undefined,
    right: undefined,
    bottom: undefined,
    border: 'none',
    borderRadius: 0,
    margin: '1rem',
    width: '100%',
    maxWidth: '520px',
    padding: '2.8rem 4rem',
    boxSizing: 'border-box',
    backgroundColor: theme.colors.white,
    boxShadow: `0px 4px 24px rgba(${rd}, ${gd}, ${bd}, 0.5)`,
  },
  overlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.8)`,
    zIndex: 11,
  },
};

const ModalTitle = styled.h1`
  font-size: 1.7rem;
  text-align: center;
  font-weight: normal;
  margin: 0 0 0.8rem 0;
  color: ${({ theme: { colors } }) => colors.blueDark};
  font-family: ${({ theme: { typography } }) => typography.museo};
`;

const ActionsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-top: 1.4rem;
`;

const ActionButton = styled.button<{ cancel?: boolean }>`
  padding: 1rem;
  outline: none;
  cursor: pointer;
  min-width: 130px;
  font-family: ${({ theme: { typography } }) => typography.museo};
  color: ${({ theme: { colors }, cancel }) =>
    cancel ? colors.white : colors.blueNeutral};
  background-color: ${({ theme: { colors }, cancel }) =>
    cancel ? colors.blueKobelco : colors.white};
  border: 0.1rem solid
    ${({ theme: { colors }, cancel }) =>
      cancel ? colors.blueKobelco : colors.blueNeutral};
`;

interface DownloadCSVModalProps {
  isOpen?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  order: ISavedOrder | null;
}

const DownloadCSVModal: FunctionComponent<DownloadCSVModalProps> = ({
  isOpen = false,
  onCancel = () => {},
  onConfirm = () => {},
  order,
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(isOpen);
  useEffect(() => {
    setModalIsOpen(isOpen);
  }, [isOpen]);

  if (order) {
    console.log('DownloadCSVModal', { isOpen, modalIsOpen, order });
    console.log(JSON.parse(order.shippingMethodRaw || '{}'));
  }

  return (
    <Modal isOpen={modalIsOpen} style={customStyles} onRequestClose={onCancel}>
      <ModalTitle>Download CSV</ModalTitle>
      <ActionsRow>
        {order && (
          <CSVLink
            headers={getExportedCartHeaders()}
            data={formatCartForExport(order)}
            filename={`order.csv`}
            target="_blank"
          >
            <ActionButton
              type="button"
              onClick={onConfirm}
              style={{ marginRight: '1.9rem' }}
            >
              Download
            </ActionButton>
          </CSVLink>
        )}
        <ActionButton cancel type="button" onClick={onCancel}>
          Done
        </ActionButton>
      </ActionsRow>
    </Modal>
  );
};

function getExportedCartHeaders() {
  return [
    { label: 'Date', key: 'date' },
    { label: 'Address', key: 'address' },
    { label: 'Customer', key: 'customer' },
    { label: 'Freight Code', key: 'freightCode' },
    { label: 'Order Type', key: 'orderType' },
    { label: 'PO Number', key: 'poNumber' },
    { label: 'Shipping Method', key: 'shippingMethod' },
    { label: 'State', key: 'state' },
    { label: 'Country', key: 'country' },
    { label: 'Total', key: 'total' },
    { label: 'Zip', key: 'zip' },
    { label: 'Parts Number', key: 'number' },
    { label: 'Parts Quantity', key: 'quantity' },
  ];
}

function formatCartForExport(cart: ISavedOrder) {
  const formattedCart = [];
  formattedCart.push({
    ...cart,
    parts: undefined,
  });

  if (cart.parts) {
    for (const part of cart.parts) {
      if (!part) continue;
      formattedCart.push({
        number: part.number,
        quantity: part.quantity,
      });
    }
  }

  return formattedCart;
}

export default DownloadCSVModal;
